'use client';

import { Button } from '@/components/ui/button';
import { Icons } from '@/components/icons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/cn';
import {
  CalendarDate,
  isToday as _isToday,
  createCalendar,
  getLocalTimeZone,
  startOfMonth,
  today,
} from '@internationalized/date';
import { nanoid } from 'nanoid';
import {
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import type { AriaButtonProps, CalendarProps, DateValue } from 'react-aria';
import {
  mergeProps,
  useButton,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useDateFormatter,
  useFocusRing,
  useLocale,
} from 'react-aria';
import type {
  CalendarState,
  DatePickerState,
  RangeCalendarState,
} from 'react-stately';
import { useCalendarState } from 'react-stately';
import { TimeField } from './time-field';

const WEEKS_IN_MONTH = 6;

const presets: Record<number, { title: string; value: () => DateValue }> = {
  1: {
    title: 'Idag',
    value: () => today(getLocalTimeZone()),
  },
  2: {
    title: 'Imorgon',
    value: () => today(getLocalTimeZone()).add({ days: 1 }),
  },
  3: {
    title: 'Sista nuvarande månad',
    value: () =>
      startOfMonth(today(getLocalTimeZone())).add({ months: 1, days: -1 }),
  },
  4: {
    title: 'Första i kommande månad',
    value: () => startOfMonth(today(getLocalTimeZone())).add({ months: 1 }),
  },
  5: {
    title: 'Sista i kommande månad',
    value: () =>
      startOfMonth(today(getLocalTimeZone())).add({ months: 2, days: -1 }),
  },
};

function Calendar({
  renderDropdownWithPortal = true,
  className,
  ...props
}: CalendarProps<DateValue> & {
  datePickerState: DatePickerState;
  renderDropdownWithPortal?: boolean;
  className?: string;
}) {
  const prevButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const [key, setKey] = useState<string>(nanoid());
  const { locale } = useLocale();
  const state = useCalendarState({
    ...props,
    locale,
    createCalendar,
  });
  const {
    calendarProps,
    prevButtonProps: _prevButtonProps,
    nextButtonProps: _nextButtonProps,
  } = useCalendar(props, state);
  const { buttonProps: prevButtonProps } = useButton(
    _prevButtonProps,
    prevButtonRef
  );
  const { buttonProps: nextButtonProps } = useButton(
    _nextButtonProps,
    nextButtonRef
  );

  const onChange = (e: string) => {
    const value = Number(e);
    const preset = presets[value];
    const date = state.focusedDate.set(preset.value());
    state.setFocusedDate(date);
    state.setValue(date);
  };

  const isDropdownValueDisabled = (value: DateValue) => {
    const toCalendarDate = new CalendarDate(value.year, value.month, value.day);
    return state.isCellUnavailable(toCalendarDate);
  };

  return (
    <div {...calendarProps} className={(cn('space-y-2'), className)}>
      <div className='flex items-center justify-between gap-2 pt-1 pb-4'>
        <div className='flex flex-1 items-center gap-2'>
          <MonthDropdown
            state={state}
            renderWithPortal={renderDropdownWithPortal}
          />
          <YearDropdown
            state={state}
            renderWithPortal={renderDropdownWithPortal}
          />
        </div>
        <Button
          {...prevButtonProps}
          onClick={(e) => {
            prevButtonProps.onClick?.(e);
            setKey(nanoid());
          }}
          ref={prevButtonRef}
          variant={'ghost'}
          size={'icon-sm'}
          className={'rounded-full'}
        >
          <Icons.previous className='size-4' />
        </Button>

        <Button
          {...nextButtonProps}
          onClick={(e) => {
            nextButtonProps.onClick?.(e);
            setKey(nanoid());
          }}
          ref={nextButtonRef}
          variant={'ghost'}
          size={'icon-sm'}
          className={'rounded-full'}
        >
          <Icons.next className='size-4' />
        </Button>
      </div>
      <CalendarGrid state={state} setKey={setKey} />
      <div className='flex gap-2 pt-2'>
        {!!props.datePickerState.hasTime && (
          <TimeField
            hideTimeZone
            value={props.datePickerState.timeValue}
            onChange={(e) => {
              if (!e) return;
              props.datePickerState.setTimeValue(e);
            }}
            shouldForceLeadingZeros={true}
          />
        )}
        <Select key={key} onValueChange={onChange}>
          <SelectTrigger size='sm'>
            <SelectValue placeholder='Välj datum' />
          </SelectTrigger>
          <SelectContent renderWithPortal={renderDropdownWithPortal}>
            {Object.entries(presets).map(([key, value]) => (
              <SelectItem
                disabled={isDropdownValueDisabled(value.value())}
                key={key}
                value={key}
              >
                {value.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

interface CalendarGridProps {
  state: CalendarState;
  setKey: Dispatch<SetStateAction<string>>;
}

function CalendarGrid({ state, setKey, ...props }: CalendarGridProps) {
  const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

  return (
    <table
      {...gridProps}
      className={cn(gridProps.className, 'w-full border-collapse space-y-1')}
    >
      <thead {...headerProps}>
        <tr className='flex'>
          {weekDays.map((day, index) => (
            <th
              className='w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground'
              key={index}
            >
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(WEEKS_IN_MONTH).keys()].map((weekIndex) => (
          <tr className={cn('mt-2 flex w-full')} key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <CalendarCell
                    key={i}
                    state={state}
                    date={date}
                    setKey={setKey}
                  />
                ) : (
                  <td key={i} />
                )
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
interface RangeCalendarGridProps {
  state: RangeCalendarState;
  setKey: Dispatch<SetStateAction<string>>;
}

function RangeCalendarGrid({
  state,
  setKey,
  ...props
}: RangeCalendarGridProps) {
  const { gridProps, headerProps, weekDays } = useCalendarGrid(props, state);

  return (
    <table
      {...gridProps}
      className={cn(gridProps.className, 'w-full border-collapse space-y-1')}
    >
      <thead {...headerProps}>
        <tr className='flex'>
          {weekDays.map((day, index) => (
            <th
              className='w-9 rounded-md text-[0.8rem] font-normal text-muted-foreground'
              key={index}
            >
              {day}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(WEEKS_IN_MONTH).keys()].map((weekIndex) => (
          <tr className={cn('mt-2 flex w-full')} key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <RangeCalendarCell
                    key={i}
                    state={state}
                    date={date}
                    setKey={setKey}
                  />
                ) : (
                  <td key={i} />
                )
              )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface CalendarCellProps {
  state: CalendarState;
  date: CalendarDate;
  setKey: Dispatch<SetStateAction<string>>;
}

function CalendarCell({ state, date, setKey }: CalendarCellProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    formattedDate,
    isUnavailable,
  } = useCalendarCell({ date }, state, ref);

  const isToday = useMemo(() => {
    const timezone = getLocalTimeZone();
    return _isToday(date, timezone);
  }, [date]);

  return (
    <td
      {...cellProps}
      className={cn(
        cellProps.className,
        'relative p-0 text-center text-sm focus-within:relative focus-within:z-20'
      )}
    >
      <Button
        {...buttonProps}
        onClick={(e) => {
          buttonProps.onClick?.(e);
          setKey(nanoid());
        }}
        type='button'
        variant={'ghost'}
        ref={ref}
        className={cn(
          buttonProps.className,
          'size-9 rounded-full',
          isToday ? 'bg-accent text-accent-foreground' : '',
          isSelected
            ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground'
            : '',
          isOutsideVisibleRange ? 'text-muted-foreground opacity-50' : '',
          isDisabled || isUnavailable
            ? 'pointer-events-none text-muted-foreground opacity-50'
            : ''
        )}
      >
        {formattedDate}
      </Button>
    </td>
  );
}

interface RangeCalendarCellProps {
  state: RangeCalendarState;
  date: CalendarDate;
  setKey: Dispatch<SetStateAction<string>>;
}

function RangeCalendarCell({ state, date, setKey }: RangeCalendarCellProps) {
  const ref = useRef<HTMLButtonElement | null>(null);
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    formattedDate,
    isUnavailable,
  } = useCalendarCell({ date }, state, ref);

  const isToday = useMemo(() => {
    const timezone = getLocalTimeZone();
    return _isToday(date, timezone);
  }, [date]);

  const isRangeStart =
    isSelected && state.highlightedRange?.start.compare(date) === 0;
  const isRangeEnd =
    isSelected && state.highlightedRange?.end.compare(date) === 0;

  return (
    <td
      {...cellProps}
      className={cn(
        cellProps.className,
        'relative p-0 text-center text-sm focus-within:relative focus-within:z-20',
        isSelected
          ? 'bg-accent text-accent-foreground first:rounded-l-full last:rounded-r-full'
          : '',
        isRangeStart ? 'rounded-l-full' : '',
        isRangeEnd ? 'rounded-r-full' : ''
      )}
    >
      <Button
        {...buttonProps}
        onClick={(e) => {
          buttonProps.onClick?.(e);
          setKey(nanoid());
        }}
        type='button'
        variant={'ghost'}
        ref={ref}
        className={cn(
          buttonProps.className,
          'size-9 rounded-full',
          isToday ? 'bg-accent text-accent-foreground' : '',
          // Range endpoints get the single calendar's selected-day styling.
          isRangeStart || isRangeEnd
            ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground'
            : '',
          isOutsideVisibleRange ? 'text-muted-foreground opacity-50' : '',
          isDisabled || isUnavailable
            ? 'pointer-events-none text-muted-foreground opacity-50'
            : ''
        )}
      >
        {formattedDate}
      </Button>
    </td>
  );
}

function MonthDropdown({
  state,
  renderWithPortal = true,
}: {
  state: CalendarState;
  renderWithPortal?: boolean;
}) {
  const months = [];
  const formatter = useDateFormatter({
    month: 'short',
    timeZone: state.timeZone,
  });

  // Format the name of each month in the year according to the
  // current locale and calendar system. Note that in some calendar
  // systems, such as the Hebrew, the number of months may differ
  // between years.
  const numMonths = state.focusedDate.calendar.getMonthsInYear(
    state.focusedDate
  );
  for (let i = 1; i <= numMonths; i++) {
    const date = state.focusedDate.set({ month: i });
    months.push(formatter.format(date.toDate(state.timeZone)));
  }

  const onChange = (e: string) => {
    const value = Number(e);
    const date = state.focusedDate.set({ month: value, day: 1 });
    state.setFocusedDate(date);
  };

  return (
    <Select onValueChange={onChange} value={state.focusedDate.month.toString()}>
      <SelectTrigger size='sm' aria-label='Month'>
        <SelectValue placeholder={months[state.focusedDate.month - 1]} />
      </SelectTrigger>
      <SelectContent renderWithPortal={renderWithPortal}>
        {months.map((month, i) => (
          <SelectItem
            onSelect={(e) => e.stopPropagation()}
            key={month}
            value={(i + 1).toString()}
          >
            {month}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
function RangeMonthDropdown({
  state,
  renderWithPortal = true,
}: {
  state: RangeCalendarState;
  renderWithPortal?: boolean;
}) {
  const months = [];
  const formatter = useDateFormatter({
    month: 'short',
    timeZone: state.timeZone,
  });

  // Format the name of each month in the year according to the
  // current locale and calendar system. Note that in some calendar
  // systems, such as the Hebrew, the number of months may differ
  // between years.
  const numMonths = state.focusedDate.calendar.getMonthsInYear(
    state.focusedDate
  );
  for (let i = 1; i <= numMonths; i++) {
    const date = state.focusedDate.set({ month: i });
    months.push(formatter.format(date.toDate(state.timeZone)));
  }

  const onChange = (e: string) => {
    const value = Number(e);
    const date = state.focusedDate.set({ month: value, day: 1 });
    state.setFocusedDate(date);
  };

  return (
    <Select onValueChange={onChange} value={state.focusedDate.month.toString()}>
      <SelectTrigger size='sm' aria-label='Month'>
        <SelectValue placeholder={months[state.focusedDate.month - 1]} />
      </SelectTrigger>
      <SelectContent renderWithPortal={renderWithPortal}>
        {months.map((month, i) => (
          <SelectItem
            onSelect={(e) => e.stopPropagation()}
            key={month}
            value={(i + 1).toString()}
          >
            {month}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function YearDropdown({
  state,
  renderWithPortal = true,
}: {
  state: CalendarState;
  renderWithPortal?: boolean;
}) {
  const years: { value: CalendarDate; formatted: string }[] = [];
  const formatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone,
  });

  // Format 100 years on each side of the current year according
  // to the current locale and calendar system.
  for (let i = -100; i <= 100; i++) {
    const date = state.focusedDate.add({ years: i });
    years.push({
      value: date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  const onChange = (e: string) => {
    const index = parseInt(e);
    const date = years[index].value;
    state.setFocusedDate(date);
  };

  return (
    <Select value={'100'} onValueChange={onChange}>
      <SelectTrigger size='sm' aria-label='Year'>
        <SelectValue placeholder={state.focusedDate.year} />
      </SelectTrigger>
      <SelectContent
        renderWithPortal={renderWithPortal}
        className='max-h-96 overflow-auto'
      >
        {years.map((year, i) => (
          <SelectItem
            onSelect={(e) => e.preventDefault()}
            key={year.formatted}
            value={i.toString()}
          >
            {year.formatted}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
function RangeYearDropdown({
  state,
  renderWithPortal = true,
}: {
  state: RangeCalendarState;
  renderWithPortal?: boolean;
}) {
  const years: { value: CalendarDate; formatted: string }[] = [];
  const formatter = useDateFormatter({
    year: 'numeric',
    timeZone: state.timeZone,
  });

  // Format 100 years on each side of the current year according
  // to the current locale and calendar system.
  for (let i = -100; i <= 100; i++) {
    const date = state.focusedDate.add({ years: i });
    years.push({
      value: date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  const onChange = (e: string) => {
    const index = parseInt(e);
    const date = years[index].value;
    state.setFocusedDate(date);
  };

  return (
    <Select value={'100'} onValueChange={onChange}>
      <SelectTrigger size='sm' aria-label='Year'>
        <SelectValue placeholder={state.focusedDate.year} />
      </SelectTrigger>
      <SelectContent
        renderWithPortal={renderWithPortal}
        className='max-h-96 overflow-auto'
      >
        {years.map((year, i) => (
          <SelectItem
            onSelect={(e) => e.preventDefault()}
            key={year.formatted}
            value={i.toString()}
          >
            {year.formatted}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function CalendarButton(props: AriaButtonProps<'button'>) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps } = useButton(props, ref);
  const { focusProps, isFocusVisible } = useFocusRing();
  return (
    <Button
      {...mergeProps(buttonProps, focusProps)}
      ref={ref}
      variant={'ghost'}
      size={'icon-sm'}
      className={`rounded-full ${isFocusVisible ? 'ring-2 ring-primary ring-offset-2' : ''}`}
    >
      {props.children}
    </Button>
  );
}

export function FieldButton(
  props: AriaButtonProps<'button'> & { isPressed: boolean }
) {
  const ref = useRef<HTMLButtonElement>(null);
  const { buttonProps, isPressed } = useButton(props, ref);
  return (
    <button
      {...buttonProps}
      ref={ref}
      className={`-ml-px rounded-r-md border px-2 outline-hidden transition-colors group-focus-within:border-violet-600 group-hover:group-focus-within:border-violet-600 ${
        isPressed
          ? 'border-gray-400 bg-gray-200'
          : 'border-gray-300 bg-subtle-light group-hover:border-gray-400'
      }`}
    >
      {props.children}
    </button>
  );
}

export {
  Calendar,
  CalendarCell,
  CalendarGrid,
  MonthDropdown,
  RangeCalendarCell,
  RangeCalendarGrid,
  RangeMonthDropdown,
  RangeYearDropdown,
  YearDropdown,
};
